import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";

import type { EmdrSetRecorder } from "../../../domain/emdrSets";
import { sudPoints } from "../../../domain/emdrSummary";
import SudCurve from "../SudCurve";

/** The session's sets so far, and how the disturbance has moved across them. */
export default function BoardHistory({ recorder }: { recorder: EmdrSetRecorder }) {
    const { t } = useTranslation();
    const { sets } = recorder;
    const points = sudPoints(sets);

    return (
        <section
            aria-labelledby="emdr-history-title"
            className="flex flex-col gap-3 rounded-3xl border border-room-line-soft bg-room-raised p-4 sm:p-5"
            data-testid="emdr-history"
        >
            <h2 id="emdr-history-title" className="text-base font-semibold text-room-ink">
                {t("emdr.console.history.title")}
            </h2>

            {sets.length === 0 ? (
                <p className="text-sm text-room-ink-muted">{t("emdr.console.history.empty")}</p>
            ) : (
                <>
                    {points.length >= 2 && (
                        <div className="text-room-ink">
                            <SudCurve points={points} />
                        </div>
                    )}
                    <ol className="flex flex-col divide-y divide-room-line-soft" aria-label={t("emdr.sets.listLabel")}>
                        {sets.map((set) => (
                            <li key={set.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
                                <span className="shrink-0 font-semibold text-room-ink sm:w-14">
                                    {t("emdr.console.history.set", { number: set.set_number })}
                                </span>
                                <span className="shrink-0 tabular-nums text-room-ink-muted sm:w-20">
                                    {t("emdr.console.history.passes", { count: set.passes })}
                                </span>
                                <span className="shrink-0 tabular-nums text-room-ink sm:w-16">{set.sud !== null ? `SUD ${set.sud}` : "—"}</span>
                                <span className="shrink-0 tabular-nums text-room-ink sm:w-16">{set.voc !== null ? `VOC ${set.voc}` : "—"}</span>
                                <span className="flex-1 sm:hidden" aria-hidden="true" />
                                <button
                                    type="button"
                                    onClick={() => void recorder.remove(set.id)}
                                    aria-label={t("emdr.sets.delete")}
                                    className="flex size-8 shrink-0 items-center justify-center rounded-lg text-room-ink-muted hover:bg-room-active hover:text-room-live sm:order-last"
                                >
                                    <Trash2 size={14} aria-hidden="true" />
                                </button>
                                {set.note && (
                                    <span className="order-last basis-full text-room-ink-muted sm:order-none sm:basis-auto sm:flex-1 sm:truncate">
                                        {set.note}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ol>
                </>
            )}
        </section>
    );
}
